import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CountryType, isSweden, LanguageType } from '@app/core/entity/locale';
import { AuthSelectors } from '@app/core/state/auth/auth.selectors';
import { FirmSelectors } from '@app/core/state/firm/firm.selectors';
import { PartnerProgramSelectors } from '@app/core/state/partnerprogram/partnerprogram.selectors';
import { openTranslatableDialog } from '@app/shared/misc/openDialog';
import { MenuItemProps, MultiLevelMenuItemProps } from '@bl/components/dist/types';
import { TranslocoModule } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api/menuitem';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatest, filter, Observable } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { UserType } from 'src/app/core/state/types';
import { BlFrontendButtonComponent } from 'src/app/core/wrappers/bl-frontend-button.component';
import { BlFrontendFloatingButtonMenuComponent } from 'src/app/core/wrappers/bl-frontend-floating-button-menu.component';
import { SnippetDirective } from 'src/app/core/wrappers/snippet-directives/snippet.directive';
import { HideIfCountriesDirective } from '../../directives/hide-if-countries.directive';
import { DueComplianceTeaserDialogComponent } from '../due-compliance-teaser-dialog/due-compliance-teaser.dialog';
import { PartnerProgramAccessDirective } from '../partner-program-access/partner-program-access.directive';
import { HelpMenuComponent } from './help-menu.component';
import { NavbarComponentTranslationType } from './types';
// import { AnalyticEventTracker, toNameFromAction } from '../../decorators/analytics.decorator';
// import { AnalyticsCategory } from 'src/app/core/entity/analytics/analytics-categories';
// import { AnalyticsAction } from 'src/app/core/entity/analytics/analytics-actions';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styles: [
    `
      .logo-text {
        font-family: 'Maison Neue Bold', sans-serif;
        font-weight: 600;
        font-size: 1.25rem;
      }
    `,
  ],
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    BlFrontendButtonComponent,
    HideIfCountriesDirective,
    PartnerProgramAccessDirective,
    TranslocoModule,
    HelpMenuComponent,
    BlFrontendFloatingButtonMenuComponent,
    SnippetDirective,
  ],
  providers: [getTranslationProvider('NavbarComponent')],
})
export class NavbarComponent extends TranslatableComponent<NavbarComponentTranslationType> implements OnInit {
  @Output() newNote = new EventEmitter<boolean>();
  @Output() settings = new EventEmitter<boolean>();
  @Output() logout = new EventEmitter<boolean>();

  loggedInUser: UserType | undefined;
  helpItems: MultiLevelMenuItemProps[];
  profileItems: MenuItem[];
  missingPartnerPackages$: Observable<number>;
  hasActiveConnection$: Observable<boolean>;

  protected profileMenuItems: MenuItemProps[];

  private i18n: NavbarComponentTranslationType;

  constructor(private dialogService: DialogService) {
    super();
    this.i18n = {} as NavbarComponentTranslationType;
    this.hasActiveConnection$ = this.store.select(FirmSelectors.selectHasActiveAmlConnection);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    combineLatest([
      this.store.select(AuthSelectors.selectAuthUser),
      this.getFirmCountryCodeObservable(),
      this.translationsLoaded$.pipe(filter(Boolean)),
    ])
      .pipe(
        filter(([user]) => Boolean(user)),
        this.takeUntilDestroyed(),
      )
      .subscribe(([user, firmCountryCode, translations]) => {
        this.loggedInUser = user;
        this.i18n = translations;
        this.profileMenuItems = this.createProfileItems(this.loggedInUser, firmCountryCode);
      });

    this.missingPartnerPackages$ = this.store.select(PartnerProgramSelectors.missingPartnerProgramPackages);
  }

  // @AnalyticEventTracker({
  //   category: AnalyticsCategory.ToDo,
  //   action: AnalyticsAction.NewNoteClicked,
  //   name: toNameFromAction(AnalyticsAction.NewNoteClicked, 'in Navbar'),
  // })
  onNewNoteClicked() {
    this.newNote.emit(true);
  }

  // @AnalyticEventTracker({
  //   category: AnalyticsCategory.FirmSettings,
  //   action: AnalyticsAction.OpenFirmSettingsDialog,
  // })
  onFirmSettingsClicked() {
    this.settings.emit(true);
  }

  logoutClicked() {
    this.logout.emit(true);
  }

  get loggedInUserFirm() {
    return this.loggedInUser.firm.name;
  }

  // @AnalyticEventTracker({
  //   category: AnalyticsCategory.PartnerProgram,
  //   action: AnalyticsAction.NavigateToPartnerProgramPage,
  //   name: toNameFromAction(AnalyticsAction.NavigateToPartnerProgramPage, 'from Navbar'),
  // })
  navigateToPartnerProgram() {
    this.router.navigate(['partnerprogram']);
  }

  onDueComplianceClick() {
    openTranslatableDialog({
      deps: {
        dialogService: this.dialogService,
        translationService: this.translationService,
        store: this.store,
      },
      componentType: DueComplianceTeaserDialogComponent,
      data: {},
    });
  }

  onProfileMenuShow() {
    // because PrimeNG doesn't support a mix of menu items with headers and without, we need to hide the empty headers
    const headers = document.querySelectorAll('#profileMenu li.p-submenu-header');
    headers.forEach((header) => {
      if (header.textContent === '') {
        header?.classList.add('hidden');
      }
    });
  }

  private createProfileItems(user: UserType | undefined, firmCountryCode: CountryType): MenuItemProps[] {
    if (!user) {
      return [];
    }

    const items: MenuItemProps[] = [
      {
        id: 'logout',
        text: this.i18n.logout,
        icon: 'arrow-right-from-bracket',
        onAction: () => {
          this.logoutClicked();
        },
      },
    ];

    if (isSweden(firmCountryCode)) {
      return items;
    }

    return [...this.createTranslationItems(this.getActiveLanguage()), ...items];
  }

  private createTranslationItems(activeLang: LanguageType): MultiLevelMenuItemProps[] {
    return [
      {
        id: 'language',
        text: this.i18n.language,
        children: [
          {
            id: 'englishLanguage',
            text: this.i18n.english,
            showCheckmark: activeLang === 'en',
            onAction: () => {
              this.setActiveLanguage('en');
            },
          },
          {
            id: 'swedishLanguage',
            text: this.i18n.swedish,
            showCheckmark: activeLang === 'sv',
            onAction: () => {
              this.setActiveLanguage('sv');
            },
          },
          {
            id: 'dutchLanguage',
            text: this.i18n.dutch,
            showCheckmark: activeLang === 'nl',
            onAction: () => {
              this.setActiveLanguage('nl');
            },
          },
        ],
      },
    ];
  }

  private getFirmCountryCodeObservable(): Observable<CountryType> {
    return this.store.select(FirmSelectors.selectCountryCode).pipe(filter(Boolean));
  }
}
